// ISO-3166 country code → canonical IANA timezone. Used to convert each
// event's local startTime to actual UTC, DST-aware for the ride date.
// Multi-zone countries (US, RU, AU, BR, CA) pick the most populous zone — a
// Perth ride will display as if it's in Sydney, but the alternative (raw
// longitude/15) drifts >1h off the real civil time in DST countries.
export const COUNTRY_TIMEZONE: Readonly<Record<string, string>> = {
  // Oceania / Pacific
  NZ: 'Pacific/Auckland',
  FJ: 'Pacific/Fiji',
  NC: 'Pacific/Noumea',
  SB: 'Pacific/Guadalcanal',
  VU: 'Pacific/Efate',
  PG: 'Pacific/Port_Moresby',
  AU: 'Australia/Sydney',
  // East / SE Asia
  KR: 'Asia/Seoul',
  JP: 'Asia/Tokyo',
  CN: 'Asia/Shanghai',
  HK: 'Asia/Hong_Kong',
  MO: 'Asia/Macau',
  MN: 'Asia/Ulaanbaatar',
  PH: 'Asia/Manila',
  SG: 'Asia/Singapore',
  MY: 'Asia/Kuala_Lumpur',
  TW: 'Asia/Taipei',
  BN: 'Asia/Brunei',
  ID: 'Asia/Jakarta',
  KH: 'Asia/Phnom_Penh',
  LA: 'Asia/Vientiane',
  TH: 'Asia/Bangkok',
  VN: 'Asia/Ho_Chi_Minh',
  MM: 'Asia/Yangon',
  BD: 'Asia/Dhaka',
  BT: 'Asia/Thimphu',
  LK: 'Asia/Colombo',
  NP: 'Asia/Kathmandu',
  IN: 'Asia/Kolkata',
  MV: 'Indian/Maldives',
  PK: 'Asia/Karachi',
  UZ: 'Asia/Tashkent',
  AF: 'Asia/Kabul',
  IR: 'Asia/Tehran',
  // Middle East
  AE: 'Asia/Dubai',
  OM: 'Asia/Muscat',
  GE: 'Asia/Tbilisi',
  AM: 'Asia/Yerevan',
  AZ: 'Asia/Baku',
  BH: 'Asia/Bahrain',
  IQ: 'Asia/Baghdad',
  KW: 'Asia/Kuwait',
  QA: 'Asia/Qatar',
  SA: 'Asia/Riyadh',
  YE: 'Asia/Aden',
  IL: 'Asia/Jerusalem',
  JO: 'Asia/Amman',
  LB: 'Asia/Beirut',
  SY: 'Asia/Damascus',
  PS: 'Asia/Gaza',
  TR: 'Europe/Istanbul',
  CY: 'Asia/Nicosia',
  // Africa
  EG: 'Africa/Cairo',
  ZA: 'Africa/Johannesburg',
  BW: 'Africa/Gaborone',
  ZW: 'Africa/Harare',
  ZM: 'Africa/Lusaka',
  MZ: 'Africa/Maputo',
  RW: 'Africa/Kigali',
  MW: 'Africa/Blantyre',
  NA: 'Africa/Windhoek',
  KE: 'Africa/Nairobi',
  TZ: 'Africa/Dar_es_Salaam',
  UG: 'Africa/Kampala',
  ET: 'Africa/Addis_Ababa',
  SO: 'Africa/Mogadishu',
  SD: 'Africa/Khartoum',
  DJ: 'Africa/Djibouti',
  ER: 'Africa/Asmara',
  MG: 'Indian/Antananarivo',
  NG: 'Africa/Lagos',
  CM: 'Africa/Douala',
  GA: 'Africa/Libreville',
  CD: 'Africa/Kinshasa',
  AO: 'Africa/Luanda',
  GH: 'Africa/Accra',
  SN: 'Africa/Dakar',
  CI: 'Africa/Abidjan',
  ML: 'Africa/Bamako',
  BF: 'Africa/Ouagadougou',
  BJ: 'Africa/Porto-Novo',
  TG: 'Africa/Lome',
  MA: 'Africa/Casablanca',
  DZ: 'Africa/Algiers',
  TN: 'Africa/Tunis',
  LY: 'Africa/Tripoli',
  // Europe
  GB: 'Europe/London',
  // UK nation pseudo-codes (England / Scotland / Wales / Northern Ireland)
  // — platform splits the UK into these for some events. All on UK time.
  XE: 'Europe/London',
  XS: 'Europe/London',
  XW: 'Europe/London',
  XI: 'Europe/London',
  IE: 'Europe/Dublin',
  PT: 'Europe/Lisbon',
  IS: 'Atlantic/Reykjavik',
  FR: 'Europe/Paris',
  ES: 'Europe/Madrid',
  IT: 'Europe/Rome',
  DE: 'Europe/Berlin',
  NL: 'Europe/Amsterdam',
  BE: 'Europe/Brussels',
  AT: 'Europe/Vienna',
  CH: 'Europe/Zurich',
  PL: 'Europe/Warsaw',
  CZ: 'Europe/Prague',
  SK: 'Europe/Bratislava',
  HU: 'Europe/Budapest',
  SE: 'Europe/Stockholm',
  NO: 'Europe/Oslo',
  DK: 'Europe/Copenhagen',
  SI: 'Europe/Ljubljana',
  HR: 'Europe/Zagreb',
  RS: 'Europe/Belgrade',
  BA: 'Europe/Sarajevo',
  ME: 'Europe/Podgorica',
  MK: 'Europe/Skopje',
  AL: 'Europe/Tirane',
  LU: 'Europe/Luxembourg',
  LI: 'Europe/Vaduz',
  MT: 'Europe/Malta',
  MC: 'Europe/Monaco',
  SM: 'Europe/San_Marino',
  VA: 'Europe/Vatican',
  AD: 'Europe/Andorra',
  FI: 'Europe/Helsinki',
  EE: 'Europe/Tallinn',
  LV: 'Europe/Riga',
  LT: 'Europe/Vilnius',
  BG: 'Europe/Sofia',
  RO: 'Europe/Bucharest',
  GR: 'Europe/Athens',
  UA: 'Europe/Kyiv',
  MD: 'Europe/Chisinau',
  BY: 'Europe/Minsk',
  RU: 'Europe/Moscow',
  // Americas — North
  CA: 'America/Toronto',
  US: 'America/New_York',
  MX: 'America/Mexico_City',
  GT: 'America/Guatemala',
  BZ: 'America/Belize',
  SV: 'America/El_Salvador',
  HN: 'America/Tegucigalpa',
  NI: 'America/Managua',
  CR: 'America/Costa_Rica',
  PA: 'America/Panama',
  CU: 'America/Havana',
  JM: 'America/Jamaica',
  HT: 'America/Port-au-Prince',
  DO: 'America/Santo_Domingo',
  PR: 'America/Puerto_Rico',
  BS: 'America/Nassau',
  BB: 'America/Barbados',
  TT: 'America/Port_of_Spain',
  // Americas — South
  CO: 'America/Bogota',
  EC: 'America/Guayaquil',
  PE: 'America/Lima',
  VE: 'America/Caracas',
  BO: 'America/La_Paz',
  PY: 'America/Asuncion',
  CL: 'America/Santiago',
  AR: 'America/Argentina/Buenos_Aires',
  UY: 'America/Montevideo',
  BR: 'America/Sao_Paulo',
  GY: 'America/Guyana',
  SR: 'America/Paramaribo',
  GF: 'America/Cayenne',
};

// Compute a timezone's UTC offset in hours for a given instant. Uses the
// platform's Intl tz database, so the result is DST-aware: e.g. Europe/Berlin
// returns +1 in January, +2 in May.
const OFFSET_FORMATTER_CACHE = new Map<string, Intl.DateTimeFormat>();

export function getOffsetHours(timeZone: string, atUtcMs: number): number {
  let fmt = OFFSET_FORMATTER_CACHE.get(timeZone);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'longOffset' });
    OFFSET_FORMATTER_CACHE.set(timeZone, fmt);
  }
  const offsetPart = fmt
    .formatToParts(new Date(atUtcMs))
    .find((p) => p.type === 'timeZoneName')?.value;
  if (!offsetPart) return 0;
  // "GMT" alone (some impls drop the +00:00 suffix), or "GMT+HH:MM" / "GMT-HH:MM"
  const match = offsetPart.match(/GMT(?:([+-])(\d{1,2})(?::(\d{2}))?)?/);
  if (!match || !match[1]) return 0;
  const sign = match[1] === '-' ? -1 : 1;
  const hours = Number(match[2]);
  const minutes = match[3] ? Number(match[3]) : 0;
  return sign * (hours + minutes / 60);
}

export function resolveEventOffsetHours(countryCode: string, atUtcMs: number): number {
  const tz = COUNTRY_TIMEZONE[countryCode.toUpperCase()];
  if (!tz) return 0;
  return getOffsetHours(tz, atUtcMs);
}
